/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  UploadResponse,
  dismissToasts,
  toast,
} from '@pypestream/design-system';
import { urqlGql } from '@pypestream/api-services';
import { tableStore } from '../stores/table-store';
import { abortWhenIdle } from './csv-request-timeout';

type UploadFileCallbacks = {
  load?: (responseText: string) => void;
  error?: (msg: string) => void;
  progress?: (computable: boolean, loaded: number, total: number) => void;
  abort?: () => void;
  onChange?: (data: { data: any }) => void;
};

type UploadFileProps = UploadFileCallbacks & {
  url: string;
  versionId: string;
  versionIdLegacy: string;
  file: File | FileList | null;
};

export const uploadCsv = ({
  url,
  file,
  versionId,
  versionIdLegacy,
  load,
  error,
  progress,
  abort,
  onChange,
}: UploadFileProps): Promise<{
  errors: string[];
  abort: () => void;
}> => {
  return new Promise((resolve) => {
    const errors: string[] = [];

    const request = new XMLHttpRequest();
    request.withCredentials = true;

    const lastServerSync = new Date().toISOString();

    request.upload.onprogress = (e) => {
      progress?.(e.lengthComputable, e.loaded, e.total);
    };

    request.onload = () => {
      const isSuccess = request.status >= 200 && request.status < 300;

      try {
        const responseJson = JSON.parse(request.response);

        if (isSuccess) {
          tableStore.lastServerSync = new Date(lastServerSync);
          tableStore.lastServerSyncError = undefined;

          dismissToasts('uploadCsv-invalid');

          load?.(request.response);

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onChange?.({ data: (responseJson as UploadResponse).data });
        } else {
          const parsedErrors = Array.isArray(responseJson.errors)
            ? responseJson.errors.map((e: unknown) =>
                typeof e === 'string' ? e : JSON.stringify(e)
              )
            : ['Unknown server error'];

          errors.push(...parsedErrors);

          tableStore.lastServerSyncError = parsedErrors;

          if (
            !toast.isActive('uploadCsv-invalid') &&
            !tableStore.errors.length
          ) {
            error?.('Upload Error');
          }
        }
      } catch (err) {
        console.error('Failed to parse upload response:', err);
        error?.('Invalid response from server');
      } finally {
        void urqlGql.updateProStudioVersionSync({
          solutionVersionId: versionId,
          projectProStudioSettingId: tableStore.projectProStudioSettingId,
          lastServerSync,
          lastServerSyncError: tableStore.lastServerSyncError,
        });

        resolve({
          errors,
          abort: () => {
            request.abort();
            abort?.();
          },
        });
      }
    };

    const onNetworkError = () => {
      console.error('Network error occurred during upload.');
      error?.('Network error during upload');
      resolve({
        errors: ['Network error'],
        abort: () => {
          request.abort();
          abort?.();
        },
      });
    };
    request.onerror = onNetworkError;

    const formData = new FormData();
    const filesArray =
      file instanceof FileList ? Array.from(file) : file ? [file] : [];

    filesArray.forEach((f) => {
      if (f) {
        formData.append('templateFile', f);
      }
    });

    // A request that stops answering is a network error, not a pass that
    // never ends (PSTUDIO-4242).
    abortWhenIdle(request, onNetworkError);
    // open() only after every upload listener is registered — some browsers
    // never fire upload progress for listeners added later (MDN), and then the
    // idle timer would abort a live upload.
    request.open('POST', url);
    request.setRequestHeader('version_id', versionIdLegacy);
    request.send(formData);
  });
};
